import { useEffect, useRef } from 'react'

import { createFreePreviewMember } from 'actions/LuxPlusActions'
import config from 'constants/config'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getChertoBucketingId } from 'lib/experiments/chertoBucketingId'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'

const COOLDOWN_MS = LUXURY_PLUS.FREE_PREVIEW_AUTO_ENROL_COOLDOWN_DAYS * 24 * 60 * 60 * 1000

/**
 * The Free Preview auto-enrolment experiment runs in GB only. Cherto targets the region too; this
 * also covers a customer who switches region after the decision was made.
 */
const FREE_PREVIEW_EXPERIMENT_REGION_CODE = 'GB'

const IS_EXPERIMENT_OVERRIDDEN_LOCALLY = config[`CHERTO_${ChertoExperiments.luxPlusFreePreviewAutoEnrol.id}`] !== undefined

function isWithinFreePreviewCooldown(lastFreePreview: App.LuxPlusLastFreePreview): boolean {
  return Date.now() < new Date(lastFreePreview.endDate).getTime() + COOLDOWN_MS
}

/** The member id Cherto was given when it made the decision held in state, if any. */
function selectFreePreviewExperimentDecisionMemberId(state: App.State): string | undefined {
  return getChertoBucketingId(state.cherto.units, ChertoExperiments.luxPlusFreePreviewAutoEnrol.id)
}

/**
 * Silently enrols a logged-in customer into the LuxPlus+ Free Preview.
 *
 * In GB this runs the Free Preview auto-enrolment Cherto experiment for every logged-in customer,
 * whether they signed up just now or years ago: the variant is enrolled, control never is. Cherto
 * buckets it by User ID, so the arm belongs to the account and a control customer who comes back is
 * still control. A customer is counted once they are logged in and eligible, the first point the
 * arms differ: at session start for a customer who arrives signed in, otherwise as they sign in or
 * up. Anonymous visitors are never counted, as they have no account to enrol or bucket yet. The
 * experiment is registered on the User ID unit, so a login refreshes its Cherto decision; enrolment
 * waits for that post-login sync, so it follows the arm the activation recorded. The decision is
 * only used when Cherto made it for the member who is logged in now: after an account switch
 * without a page load, a failed refresh leaves the previous member's arm in state.
 *
 * Elsewhere it enrols an existing customer whose Marketing Router variant carries the
 * `free-preview-auto-enrol-signin` treatment (resolved on the server into
 * `marketing.freePreviewAutoEnrolForExistingUsers`). That path keys off *being* logged in rather
 * than off a login event: the campaigns that target existing customers are mostly EDM traffic, and
 * most of those visitors arrive with a live session, so they never produce a login event to hang
 * the enrolment off. Customers who registered in this session are left to the new-user path in
 * userMiddleware, so the two router audiences stay disjoint.
 *
 * Each enrolment carries the trigger the celebration modal reads to decide whether to call the
 * preview a welcome gift. In the experiment that is anyone whose account was created in this tab.
 */
function useLuxPlusFreePreviewAutoEnrolExistingUsers() {
  const dispatch = useAppDispatch()
  const loggedIn = useAppSelector(selectLoggedIn)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const marketingRouterSettingEnabled = useAppSelector(state => state.marketing.freePreviewAutoEnrolForExistingUsers?.enabled ?? false)
  const isInExperimentRegion = useAppSelector(state => state.geo.currentRegionCode === FREE_PREVIEW_EXPERIMENT_REGION_CODE)
  const isPostLoginExperimentSyncing = useAppSelector(state => state.optimizely.isPostLoginExperimentSyncing)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const decisionMemberId = useAppSelector(selectFreePreviewExperimentDecisionMemberId)
  // Marketing Router path only: keeps its existing-customer audience apart from the sign-up path.
  const isNewUser = useAppSelector(state => state.loyalty.enrolment === 'newUserRegistration')
  const justSignedUp = useAppSelector(state => state.auth.justSignedUp)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const subscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription)
  const { canJoinFreePreview } = useLuxPlusFreePreviewUtils()
  const enrolRequestedRef = useRef(false)

  const isWithinCooldownInAnyRegion = subscription.lastFreePreviews?.some(isWithinFreePreviewCooldown) ?? false
  // canJoinFreePreview is region-scoped, so a live preview from another region doesn't register
  // there. The cooldown covers this too, but only once svc-membership returns the history.
  const hasLiveFreePreview = subscription.item?.tier === 'free_preview'

  // Reads no experiment variation, so both arms of the experiment activate on the same audience.
  const isEligibleCustomer =
    loggedIn &&
    luxPlusEnabled &&
    !isSpoofed &&
    canJoinFreePreview &&
    !hasLiveFreePreview &&
    !isWithinCooldownInAnyRegion &&
    // createFreePreviewMember bails while the membership is still loading, so waiting for it to
    // settle keeps us from spending the single attempt on a guaranteed no-op.
    !subscription.fetching &&
    !subscription.error

  // The experiment covers the audiences of both brand switches, so either one turns it off.
  const isExperimentEnabledForBrand =
    config.ENABLE_AUTO_ENROLLMENT_FOR_FREE_PREVIEW &&
    config.ENABLE_AUTO_ENROLLMENT_FOR_FREE_PREVIEW_EXISTING_USERS
  // The Cherto state survives logout and a failed post-login refresh, so the arm in it may belong
  // to the member who was logged in before. Counting or enrolling this member on that arm would
  // record them under the other member's bucketing id. A local override has no unit to compare.
  const isDecisionForCurrentMember = IS_EXPERIMENT_OVERRIDDEN_LOCALLY || (!!memberId && decisionMemberId === memberId)
  const { isTurnedOn, isAnyVariant } = useExperiment(ChertoExperiments.luxPlusFreePreviewAutoEnrol, {
    canActivate: isEligibleCustomer && isInExperimentRegion && isExperimentEnabledForBrand && isDecisionForCurrentMember,
  })

  // A customer in the experiment gets its arm, so control stays control even while a Marketing
  // Router variant still carries the treatment. A decision that belongs to another member blocks
  // enrolment outright rather than handing the customer to the router.
  const isInExperiment = isInExperimentRegion && isTurnedOn
  const isTreated = isInExperiment ?
    isExperimentEnabledForBrand && isAnyVariant && isDecisionForCurrentMember :
    config.ENABLE_AUTO_ENROLLMENT_FOR_FREE_PREVIEW_EXISTING_USERS && !isNewUser && marketingRouterSettingEnabled
  // Only the experiment region waits for the post-login sync; elsewhere the Marketing Router path
  // is as before.
  const isAwaitingExperimentRebucket = isInExperimentRegion && isPostLoginExperimentSyncing

  const shouldEnrol = isEligibleCustomer && isTreated && !isAwaitingExperimentRebucket
  // The Marketing Router path only ever enrols existing customers.
  const trigger = isInExperiment && justSignedUp ?
    'auto-free-preview-enroll' :
    'auto-free-preview-enroll-for-existing-user'

  useEffect(() => {
    if (!shouldEnrol || enrolRequestedRef.current) return

    // One attempt per session. createFreePreviewMember() no-ops for members who can already view
    // benefits; anything else that slips past the checks above is svc-membership's call to
    // accept or reject. Failures stay silent because the customer never asked for this.
    enrolRequestedRef.current = true
    dispatch(createFreePreviewMember({
      trigger,
      silentError: true,
    }))
  }, [dispatch, shouldEnrol, trigger])
}

export default useLuxPlusFreePreviewAutoEnrolExistingUsers
